import clsx from 'clsx'
import {FC, useEffect, useState, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {Button} from '../../../../app/components/Button/Button'
import {LanguageList} from '../../../../app/components/Modals/Topbar/LanguageList/LanguageList'
import useAuth from '../../../../app/hooks/useAuth'
import {ManualList} from '../../../../app/components/Modals/Topbar/ManualList/ManualList'
import {toAbsoluteUrl} from '../../../helpers'
import {use} from 'i18next'
import {GET} from '../../../../app/services/api'
import {Loading} from '../../../../../src/app/components/Loading/Loading'

const toolbarButtonMarginClass = 'ms-1 ms-lg-3',
  toolbarUserAvatarHeightClass = 'symbol-30px symbol-md-40px'

const Topbar: FC = () => {
  const navigate = useNavigate()
  const {loginInfo, verifyAdm} = useAuth()
  const [listLanguages, setListLanguages] = useState<any[]>([])
  const [loading, setLoading] = useState(true) // Inicia com loading ativo
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const hideTimerRef = useRef<number | null>(null)
  const languageMenuRef = useRef<HTMLDivElement | null>(null)
  const [pinned, setPinned] = useState(false)

  const getLanguage = () => {
    // Verifica tanto o localStorage quanto o i18n atual
    const storedLanguage = localStorage.getItem('i18nextLng')

    switch (storedLanguage) {
      case 'pt': // ← Mudança: código simples
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/br.svg')}
            alt=''
          />
        )
      case 'en': // ← Mudança: código simples
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/en.svg')}
            alt=''
          />
        )
      case 'es': // ← Mudança: código simples
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/es.svg')}
            alt=''
          />
        )
      case 'fr': // ← Mudança: código simples
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/fr.svg')}
            alt=''
          />
        )
      case 'de':
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/de.svg')}
            alt=''
          />
        )
      case 'it':
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/it.svg')}
            alt=''
          />
        )
      case 'ja':
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/ja.svg')}
            alt=''
          />
        )
      case 'ko':
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/ko.svg')}
            alt=''
          />
        )
      case 'zh':
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/zh.svg')}
            alt=''
          />
        )
      default:
        return (
          <img
            className='h-20px w-20px rounded cursor-pointer'
            src={toAbsoluteUrl('/media/svg/flags/br.svg')}
            alt=''
          />
        )
    }
  }

  const getLanguagePluginspace = async () => {
    const fallbackLanguage = {name: 'Português', language: 'br'}

    const extractLanguages = (item: any) => {
      if (!item || !item.languages || !Array.isArray(item.languages)) {
        throw new Error('Invalid item structure in /hotel-translation response')
      }

      return item.languages
        .filter((l: any) => l && l.status === true)
        .map((l: any) => ({
          name: l.description ?? l.name,
          language: l.code === 'pt' ? 'br' : l.code,
        }))
    }

    try {
      const response = await GET('/hotel-translation')

      if (!response || !response.data) {
        throw new Error('No data received from /hotel-translation')
      }

      const newLanguages = response.data.flatMap((item: any) => extractLanguages(item))

      // verifica idioma atual no localStorage
      const currentLang = localStorage.getItem('i18nextLng')
      const currentLangAsDisplay = currentLang === 'pt' ? 'br' : currentLang
      const isCurrentLangAvailable = newLanguages.some(
        (lang: any) => lang.language === currentLangAsDisplay
      )

      if (newLanguages.length > 0) {
        if (!isCurrentLangAvailable) {
          const firstLanguage = newLanguages[0].language
          const langToSet = firstLanguage === 'br' ? 'pt' : firstLanguage

          localStorage.setItem('i18nextLng', langToSet)
          localStorage.setItem('i18nConfig', JSON.stringify({selectedLang: langToSet}))

          // atualiza i18n dinamicamente
          await import('../../../../app/translate/i18n').then(({i18n}) =>
            i18n.changeLanguage(langToSet)
          )
        }
      } else {
        // sem idiomas disponíveis: força português
        localStorage.setItem('i18nextLng', 'pt')
        localStorage.setItem('i18nConfig', JSON.stringify({selectedLang: 'pt'}))
        newLanguages.push(fallbackLanguage)

        await import('../../../../app/translate/i18n').then(({i18n}) => i18n.changeLanguage('pt'))
      }

      setListLanguages(newLanguages)
    } catch (error) {
      // mantém apenas logs de erro
      console.error('Error fetching language plugin space:', error)

      // fallback: português
      localStorage.setItem('i18nextLng', 'pt')
      localStorage.setItem('i18nConfig', JSON.stringify({selectedLang: 'pt'}))
      setListLanguages([fallbackLanguage])

      // tenta garantir que o i18n esteja em pt, mas não propaga se falhar
      try {
        const {i18n} = await import('../../../../app/translate/i18n')
        await i18n.changeLanguage('pt')
      } catch {
        /* swallow: já logamos o erro principal acima */
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getLanguagePluginspace()
  }, [])

  // Fecha o menu quando clicar fora; também desfaz o pinned
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const el = languageMenuRef.current
      if (!el) return
      if (!el.contains(e.target as Node)) {
        setShowLanguageMenu(false)
        setPinned(false)
      }
    }

    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Reinicializa os menus Metronic quando a lista de idiomas muda
  useEffect(() => {
    if (!loading && listLanguages.length > 0) {
      setTimeout(() => {
        // Reinicializa menus hover do Metronic
        if ((window as any).KTMenu) {
          ;(window as any).KTMenu.createInstances()
        }
      }, 100)
    }
  }, [loading, listLanguages])

  return loading ? (
    <Loading />
  ) : (
    <div className='d-flex align-items-center justify-content-between flex-shrink-1'>
      {/* <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <div
          data-kt-menu-trigger='click'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
          data-kt-menu-flip='bottom'
        >
          <Button
            text='Configurações'
            color='info'
            iconColor='#FFF'
            icon='fas fa-bars'
            size='small'
            horizontalPadding={4}
            verticalPadding={3}
            btnAttr={{
              type: 'button',
            }}
            click={() => {}}
            uppercase={true}
            btnClass='me-2 fs-thin'
          />
        </div>
        <ConfigurationsList
          data={[
            {name: 'Biblioteca de ícones', link: '/#'},
            {name: 'Biblioteca de ícones', link: '/#'},
            {name: 'Biblioteca de ícones', link: '/#'},
          ]}
        />
      </div> */}

      {verifyAdm() && (
        <Button
          text='Pluginspaces'
          color='info'
          iconColor='#FFF'
          icon='fas fa-store'
          size='small'
          horizontalPadding={4}
          verticalPadding={3}
          btnAttr={{
            type: 'button',
          }}
          click={() => navigate('/pluginspace')}
          uppercase={true}
          btnClass='mx-4 fs-thin'
        />
      )}

      <Button
        text={loginInfo.pluginspace}
        color='primary'
        iconColor='#FFF'
        icon='fas fa-home'
        size='small'
        horizontalPadding={4}
        verticalPadding={3}
        btnAttr={{
          type: 'button',
        }}
        click={() => navigate('/dashboard')}
        uppercase={true}
        btnClass='mx-4 fs-thin'
      />

      {/*
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <div
          data-kt-menu-trigger='click'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
          data-kt-menu-flip='bottom'
        >
          <Button 
            text="Ads Calçados"
            color="secondary"
            iconColor='#FFF'
            icon='fas fa-home'
            size='small'
            horizontalPadding={4}
            verticalPadding={3}
            btnAttr={{
              type: 'button'
            }}
            click={() => {}}
            uppercase={true}
            btnClass="me-2 fs-thin"
          />
        </div>
        <PluginspaceList data={[
          { color: "#000", name: "ADS Calçados" },
          { color: "#f00", name: "Bar do Adao" },
          { color: "#0f0", name: "Baroba" },
          { color: "#0ff", name: "Athuz" },
          { color: "#b0c", name: "Teste" },
          { color: "#ff3", name: "Bar do ze" },
        ]} />
      </div>
      */}

      <div
        ref={languageMenuRef}
        className={clsx('d-flex align-items-center', toolbarButtonMarginClass, 'position-relative')}
        onMouseEnter={() => {
          if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current)
            hideTimerRef.current = null
          }
          setShowLanguageMenu(true)
        }}
        onMouseLeave={() => {
          if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
          hideTimerRef.current = window.setTimeout(() => {
            if (!pinned) setShowLanguageMenu(false)
          }, 250)
        }}
      >
        <div
          className='bg-hover-light w-40px h-40px rounded cursor-pointer d-flex align-items-center justify-content-center'
          onClick={() => {
            // toggle pinned on click; pinned keeps menu open until outside click
            if (pinned) {
              setPinned(false)
              setShowLanguageMenu(false)
            } else {
              setPinned(true)
              setShowLanguageMenu(true)
              if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current)
                hideTimerRef.current = null
              }
            }
          }}
        >
          {getLanguage()}
          {showLanguageMenu && (
            <div
              className='position-absolute bg-white border rounded shadow-sm'
              style={{top: '100%', right: 0, zIndex: 9999, minWidth: '175px'}}
              onMouseEnter={() => {
                if (hideTimerRef.current) {
                  clearTimeout(hideTimerRef.current)
                  hideTimerRef.current = null
                }
              }}
              onMouseLeave={() => {
                if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
                hideTimerRef.current = window.setTimeout(() => {
                  if (!pinned) setShowLanguageMenu(false)
                }, 250)
              }}
            >
              <LanguageList data={listLanguages} />
            </div>
          )}
        </div>
      </div>
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <div
          data-kt-menu-trigger='hover'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
          data-kt-menu-flip='bottom'
          className='bg-hover-light w-40px h-40px rounded cursor-pointer d-flex align-items-center justify-content-center'
        >
          <i className='bi bi-question-circle fs-2 text-gray-700'></i>
        </div>
        <ManualList />
      </div>

      {/* begin::User */}
      <div
        className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}
        id='kt_sidebar_perfil_toggle'
      >
        {/* begin::Toggle */}
        <div
          className={clsx('cursor-pointer symbol', toolbarUserAvatarHeightClass)}
          data-kt-menu-trigger='click'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
          data-kt-menu-flip='bottom'
        >
          <div
            className='btn btn-icon w-auto btn-clean d-flex align-items-center btn-lg px-2 bg-hover-light'
            id='kt_quick_user_toggle'
          >
            <span className='text-muted fw-bold fs-base d-none d-md-inline me-1'>Olá,</span>
            <span className='text-gray-600 fw-bolder fs-base d-none d-md-inline me-3'>
              {loginInfo.name}
            </span>
          </div>
        </div>
        {/* end::Toggle */}
      </div>
      {/* end::User */}
    </div>
  )
}

export {Topbar}
